import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { ROUTES } from "config/routes";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

import BatchSummaryDashboard from "./components/BatchSummaryDashboard";
import BatchFilters from "./components/BatchFilters";
import BatchCard from "./components/BatchCard";
import BatchTable from "./components/BatchTable";
import { useGetBatchStatus } from "./useGetBatchStatus";

const BatchProcessingOverviewContent = () => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();

  const [viewMode, setViewMode] = useState("table");
  const [sortField, setSortField] = useState("uploadTime");
  const [sortDirection, setSortDirection] = useState("desc");

  const { data: batchInfo, refetch } = useGetBatchStatus();

  const [filters, setFilters] = useState({
    status: "all",
    size: "all",
    search: "",
    dateRange: {
      start: "",
      end: "",
    },
  });

  const navigationItems = [
    {
      label: tCommon("navigation.processingQueue"),
      path: ROUTES.INVOICE_QUEUE,
      icon: "Clock",
      description: tCommon("navigation.processingQueueDescription"),
    },
    {
      label: tCommon("navigation.errorCorrection"),
      path: ROUTES.INVOICE_CORRECTION,
      icon: "AlertTriangle",
      description: tCommon("navigation.errorCorrectionDescription"),
    },
    {
      label: tCommon("navigation.batchOverview"),
      path: ROUTES.INVOICE_UPLOAD,
      icon: "Layers",
      description: tCommon("navigation.batchOverviewDescription"),
    },
    {
      label: tCommon("navigation.completionSummary"),
      path: ROUTES.INVOICE_COMPLETION,
      icon: "CheckCircle",
      description: tCommon("navigation.completionSummaryDescription"),
    },
  ];

  const batches = React.useMemo(() => {
    if (!batchInfo || !Array.isArray(batchInfo)) {
      return [];
    }

    return batchInfo.map((batch) => {
      const totalCount = batch.files?.length || 0;

      const processedCount =
        batch.files?.filter((file) => file.status !== "pending")?.length || 0;

      const successCount =
        batch.files?.filter((file) => file.status === "completed")?.length || 0;

      const failureCount =
        batch.files?.filter((file) => file.status === "failed")?.length || 0;

      let status = "pending";

      if (processedCount === totalCount && totalCount > 0) {
        status = failureCount > 0 ? "failed" : "completed";
      } else if (processedCount > 0) {
        status = "processing";
      }

      const uploadTime = new Date(batch.batchUploadedAt).toLocaleString(
        "en-US",
        {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
      );

      const invoices =
        batch.files?.map((file) => ({
          id: file.recordId,
          filename: file.filename,
          status: file.status,
          currentStep:
            file.status === "completed"
              ? "Completed"
              : file.status === "failed"
                ? "Failed"
                : file.status === "processing"
                  ? "Processing"
                  : "Pending",
          fileSize: file.fileSize,
          progress: file.progress || null,
        })) || [];

      return {
        id: batch.batchId,
        batchName: batch.batchName,
        uploadTime,
        totalCount,
        processedCount,
        successCount,
        failureCount,
        status,
        estimatedTime:
          status === "completed"
            ? "Completed"
            : status === "failed"
              ? "Failed"
              : status === "processing"
                ? "Processing..."
                : "Pending",
        currentStep:
          status === "completed"
            ? "Completed"
            : status === "failed"
              ? "Multiple Errors"
              : status === "processing"
                ? "Processing"
                : "Pending",
        invoices,
      };
    });
  }, [batchInfo]);

  const summaryStats = {
    totalBatches: batches.length,
    activeBatches: batches.filter((batch) => batch.status === "processing")
      .length,
    completedToday: batches.filter((batch) => batch.status === "completed")
      .length,
    failedBatches: batches.filter((batch) => batch.status === "failed").length,
    totalInvoices: batches.reduce((sum, batch) => sum + batch.totalCount, 0),
    avgProcessingTime: "18 min",
  };

  const activeBatchesByStatus = {
    processing: batches.filter((batch) => batch.status === "processing").length,
    completed: batches.filter((batch) => batch.status === "completed").length,
    failed: batches.filter((batch) => batch.status === "failed").length,
    paused: batches.filter((batch) => batch.status === "paused").length,
  };

  const getFilteredBatches = () => {
    let filtered = [...batches];

    if (filters.status !== "all") {
      filtered = filtered.filter((batch) => batch.status === filters.status);
    }

    if (filters.size !== "all") {
      filtered = filtered.filter((batch) => {
        switch (filters.size) {
          case "small":
            return batch.totalCount <= 10;

          case "medium":
            return batch.totalCount > 10 && batch.totalCount <= 50;

          case "large":
            return batch.totalCount > 50;

          default:
            return true;
        }
      });
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();

      filtered = filtered.filter(
        (batch) =>
          batch.id?.toLowerCase().includes(search) ||
          batch.batchName?.toLowerCase().includes(search),
      );
    }

    if (filters.dateRange.start || filters.dateRange.end) {
      filtered = filtered.filter((batch) => {
        const batchDate = new Date(batch.uploadTime);

        const startDate = filters.dateRange.start
          ? new Date(filters.dateRange.start)
          : null;

        const endDate = filters.dateRange.end
          ? new Date(filters.dateRange.end)
          : null;

        if (startDate && batchDate < startDate) {
          return false;
        }

        if (endDate && batchDate > endDate) {
          return false;
        }

        return true;
      });
    }

    filtered.sort((firstBatch, secondBatch) => {
      let firstValue = firstBatch?.[sortField];
      let secondValue = secondBatch?.[sortField];

      if (sortField === "progress") {
        firstValue = (firstBatch.processedCount / firstBatch.totalCount) * 100;

        secondValue =
          (secondBatch.processedCount / secondBatch.totalCount) * 100;
      }

      if (typeof firstValue === "string") {
        firstValue = firstValue.toLowerCase();
        secondValue = secondValue?.toLowerCase();
      }

      if (sortDirection === "asc") {
        return firstValue > secondValue ? 1 : -1;
      }

      return firstValue < secondValue ? 1 : -1;
    });

    return filtered;
  };

  const filteredBatches = getFilteredBatches();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const handlePauseBatch = (batchId) => {
    console.log("Pausing batch:", batchId);
  };

  const handleResumeBatch = (batchId) => {
    console.log("Resuming batch:", batchId);
  };

  const handleDownloadResults = (batchId) => {
    console.log("Downloading results for batch:", batchId);
  };

  const handleViewDetails = (batchId) => {
    navigate(ROUTES.INVOICE_COMPLETION, {
      state: { batchId },
    });
  };

  const handleCreateBatch = () => {
    navigate(ROUTES.INVOICE_QUEUE);
  };

  const handleClearFilters = () => {
    setFilters({
      status: "all",
      size: "all",
      search: "",
      dateRange: {
        start: "",
        end: "",
      },
    });
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <Helmet>
        <title>
          {`${t("batchOverview.title")} - ZATCA Compliance Management`}
        </title>

        <meta name="description" content={t("batchOverview.metaDescription")} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <Header
          title={t("batchOverview.title")}
          description={t("batchOverview.description")}
          customNavigationItems={navigationItems}
        />

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.1,
          }}
          className="mb-8"
        >
          <BatchSummaryDashboard summaryStats={summaryStats} />
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.2,
          }}
          className="mb-6"
        >
          <BatchFilters
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            onCreateBatch={handleCreateBatch}
            totalBatches={batches.length}
            activeBatches={activeBatchesByStatus}
          />
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.3,
          }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {t("batchOverview.filteredCount", {
                    filtered: filteredBatches.length,
                    total: batches.length,
                  })}
                </span>

                {filters.status !== "all" ||
                filters.size !== "all" ||
                filters.search ||
                filters.dateRange.start ||
                filters.dateRange.end ? (
                  <span className="text-sm text-muted-foreground">
                    ({t("common.filtered")})
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "cards" ? "default" : "outline"}
                size="sm"
                iconName="Grid3X3"
                onClick={() => setViewMode("cards")}
              >
                {t("batchOverview.cards")}
              </Button>

              <Button
                variant={viewMode === "table" ? "default" : "outline"}
                size="sm"
                iconName="Table"
                onClick={() => setViewMode("table")}
              >
                {t("batchOverview.table")}
              </Button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.2,
          }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{
                opacity: 0,
                x: 20,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              exit={{
                opacity: 0,
                x: -20,
              }}
              transition={{
                duration: 0.3,
              }}
            >
              {filteredBatches.length === 0 ? (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
                    <Icon
                      name="Layers"
                      size={32}
                      className="text-muted-foreground"
                    />
                  </div>

                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t("batchOverview.emptyTitle")}
                  </h3>

                  <p className="text-muted-foreground mb-6">
                    {filters.status !== "all" ||
                    filters.size !== "all" ||
                    filters.search ||
                    filters.dateRange.start ||
                    filters.dateRange.end
                      ? t("common.adjustFilters")
                      : t("batchOverview.createFirst")}
                  </p>

                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={handleCreateBatch}
                  >
                    {t("actions.createNewBatch")}
                  </Button>
                </div>
              ) : viewMode === "cards" ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredBatches.map((batch) => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onPause={handlePauseBatch}
                      onResume={handleResumeBatch}
                      onDownload={handleDownloadResults}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              ) : (
                <BatchTable
                  batches={filteredBatches}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onPause={handlePauseBatch}
                  onResume={handleResumeBatch}
                  onDownload={handleDownloadResults}
                  onViewDetails={handleViewDetails}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

const BatchProcessingOverview = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <BatchProcessingOverviewContent />
      </div>
    </SidebarProvider>
  );
};

export default BatchProcessingOverview;
